import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { pdfStyles } from '../PDFStyles';
import { preprocessTextForPDF } from '../EmojiProcessor';

interface SummarySectionProps {
  forecast: any;
  displayResults: any;
  isFirstPage?: boolean;
  isSecondPage?: boolean;
}

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string?: string | null) => {
  if (!string || typeof string !== 'string') return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const SummarySection: React.FC<SummarySectionProps> = ({ 
  forecast, 
  displayResults, 
  isFirstPage = false,
  isSecondPage = false 
}) => {
  // First page content
  if (isFirstPage) {
    return (
      <View style={pdfStyles.card}>
        <Text style={pdfStyles.cardTitle}>Detailed Analysis</Text>
        
        {forecast?.eventType && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Event Type</Text>
            <Text style={pdfStyles.analysisText}>{capitalizeFirstLetter(forecast.eventType)}</Text>
          </View>
        )}

        {forecast?.baseline && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Audience Baseline</Text>
            <Text style={pdfStyles.analysisText}>{preprocessTextForPDF(forecast.baseline)}</Text>
          </View>
        )}

        {forecast?.delta && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Analysis</Text>
            <Text style={pdfStyles.analysisText}>{preprocessTextForPDF(forecast.delta)}</Text>
          </View>
        )}

        {(displayResults?.imageDescription?.alt || (displayResults?.imageDescription?.notableDeviations && displayResults.imageDescription.notableDeviations.length > 0)) && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Image Analysis</Text>
            
            {displayResults?.imageDescription?.alt && (
              <Text style={pdfStyles.analysisText}>{preprocessTextForPDF(displayResults.imageDescription.alt)}</Text>
            )}
            
            {displayResults?.imageDescription?.notableDeviations && displayResults.imageDescription.notableDeviations.length > 0 && (
              <View style={pdfStyles.analysisSection}>
                <Text style={pdfStyles.analysisSubtitle}>Notable Deviations</Text>
                <View style={pdfStyles.analysisText}>
                  {displayResults.imageDescription.notableDeviations.map((deviation: string, index: number) => (
                    <Text key={index} style={[pdfStyles.analysisText, { marginLeft: 16, marginBottom: 4 }]}>
                      • {preprocessTextForPDF(deviation)}
                    </Text>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    );
  }

  // Second page content
  if (isSecondPage) {
    return (
      <View style={pdfStyles.card}>
        <Text style={pdfStyles.cardTitle}>Detailed Analysis (continued)</Text>

        {forecast?.driversPositive && forecast.driversPositive.length > 0 && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Positive Drivers</Text>
            <View style={pdfStyles.chipContainer}>
              {forecast.driversPositive.map((driver: string, index: number) => (
                <View key={index} style={pdfStyles.chip}>
                  <Text style={pdfStyles.chipText}>{preprocessTextForPDF(driver)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {forecast?.driversNegative && forecast.driversNegative.length > 0 && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Negative Drivers</Text>
            <View style={pdfStyles.chipContainer}>
              {forecast.driversNegative.map((driver: string, index: number) => (
                <View key={index} style={[pdfStyles.chip, pdfStyles.negativeChip]}>
                  <Text style={[pdfStyles.chipText, pdfStyles.negativeChipText]}>{preprocessTextForPDF(driver)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {forecast?.recommendations && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Recommendations</Text>
            <Text style={pdfStyles.analysisText}>{preprocessTextForPDF(forecast.recommendations)}</Text>
          </View>
        )}

        {/* Engagement Forecast */}
        {(forecast?.replies1d || forecast?.replies7d || forecast?.likes7d) && (
          <View style={pdfStyles.analysisSection}>
            <Text style={pdfStyles.analysisSubtitle}>Engagement Forecast</Text>
            <View style={pdfStyles.engagementGrid}>
              {forecast?.replies1d !== undefined && (
                <View style={pdfStyles.engagementItem}>
                  <Text style={pdfStyles.engagementNumber}>{forecast.replies1d}</Text>
                  <Text style={pdfStyles.engagementLabel}>First 24h Replies</Text>
                </View>
              )}
              {forecast?.replies7d !== undefined && (
                <View style={pdfStyles.engagementItem}>
                  <Text style={pdfStyles.engagementNumber}>{forecast.replies7d}</Text>
                  <Text style={pdfStyles.engagementLabel}>7-Day Replies</Text>
                </View>
              )}
              {forecast?.likes7d !== undefined && (
                <View style={pdfStyles.engagementItem}>
                  <Text style={pdfStyles.engagementNumber}>{forecast.likes7d}</Text>
                  <Text style={pdfStyles.engagementLabel}>7-Day Likes</Text>
                </View>
              )}
            </View>
          </View>
        )}
      </View>
    );
  }

  // Default empty view if neither flag is set
  return null;
};